import { Component, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { FrequencyType } from '../enums/frequency-type.enum';
import { DaysType } from '../enums/days-type.enum';
import { atLeastOneDaySelected } from './schedule-order-dialog.custom.validators';
import { CurrentOrderService } from '../services/current-order.service';
import { TranslationService } from 'projects/dop-core/src/public-api';
import { MonthlySelect } from '../enums/monthly-select.enum';
import { RunConfig, ScheduleOrder } from '../models/my-orders/schedule-order-details.model';
import { OrderCommandService } from '../data-service/order-command.service';

@Component({
  selector: 'app-schedule-order-dialog',
  templateUrl: './schedule-order-dialog.component.html',
  styleUrls: ['./schedule-order-dialog.component.scss'],
})
export class ScheduleOrderDialogComponent implements OnInit {
  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fb: FormBuilder,
    private currentOrder: CurrentOrderService,
    private orderCommandService: OrderCommandService,
  ) { 
    this.DataToBePopulated = this.config?.data;
  }

  disabledSaveButton = true;
  scheduleForm: FormGroup;
  minStartDate: Date;
  minEndDate: Date;
  FrequencyType = FrequencyType;
  scheduleOrderInfo: string = TranslationService.translate('vbeiScheduleOrderInfo').replace(/\r\n/g, '\r\n\r\n');
  MonthlySelect = MonthlySelect;
  DataToBePopulated: any;

  daysOfWeek = [
    { label: TranslationService.translate('vbeiSunday'), value: DaysType.Sunday },
    { label: TranslationService.translate('vbeiMonday'), value: DaysType.Monday },
    { label: TranslationService.translate('vbeiTuesday'), value: DaysType.Tuesday },
    { label: TranslationService.translate('vbeiWednesday'), value: DaysType.Wednesday },
    { label: TranslationService.translate('vbeiThursday'), value: DaysType.Thursday },
    { label: TranslationService.translate('vbeiFriday'), value: DaysType.Friday },
    { label: TranslationService.translate('vbeiSaturday'), value: DaysType.Saturday },
  ];

  weeksOfMonth = [
    { label: TranslationService.translate('vbeiFirst'), value: 1 },
    { label: TranslationService.translate('vbeiSecond'), value: 2 },
    { label: TranslationService.translate('vbeiThird'), value: 3 },
    { label: TranslationService.translate('vbeiFourth'), value: 4 },
    { label: TranslationService.translate('Last'), value: 5 },
  ]


  async ngOnInit(): Promise<void> {
    this.initializeForm();
    this.setUpFormStatusChange();
    this.scheduleForm.markAllAsTouched();
    this.setUpValueChanges();
    const selectedDateControl = this.scheduleForm.get('selectedDate');
    const selectedWeekControl = this.scheduleForm.get('selectedWeek');
    const selectedDayControl = this.scheduleForm.get('selectedDay');
    selectedWeekControl?.disable();
    selectedDayControl?.disable();
    this.scheduleForm.get('monthlySelect')?.valueChanges.subscribe(value => {
      if (value === MonthlySelect.WeekAndDaySelected) {
        selectedDateControl?.disable();
        selectedWeekControl?.enable();
        selectedDayControl?.enable();
      } else {
        selectedDateControl?.enable();
        selectedWeekControl?.disable();
        selectedDayControl?.disable();
      }
    });
    if(this.DataToBePopulated){
      this.populateDataToScheduleOrderDialog();
    }
  }

  populateDataToScheduleOrderDialog(){
    this.scheduleForm.get('scheduleName').setValue(this.DataToBePopulated?.event?.scheduleName);
    switch(this.DataToBePopulated?.event?.frequencyType) {
      case "Daily":
        this.scheduleForm.get('frequencyType').setValue(1);
        break;
      case "Weekly":
        this.scheduleForm.get('frequencyType').setValue(2);
        let selectedDays = [false, false, false, false, false, false, false];
        this.DataToBePopulated?.event?.runConfig?.selectedDays.forEach(day => {
          selectedDays[day] = true;
        });
        this.scheduleForm.get('selectedDays').setValue(selectedDays);
        break;
      case "Monthly":
        this.scheduleForm.get('frequencyType').setValue(3);
        const selectedDateControl = this.scheduleForm.get('selectedDate');
        const selectedWeekControl = this.scheduleForm.get('selectedWeek');
        const selectedDayControl = this.scheduleForm.get('selectedDay');
        if(this.DataToBePopulated?.event?.runConfig?.selectedDate){
          this.scheduleForm?.get('monthlySelect').setValue(MonthlySelect.DateSelected);
          this.scheduleForm.get('selectedDate').setValue(this.DataToBePopulated?.event?.runConfig?.selectedDate);
          selectedDateControl?.enable();
          selectedWeekControl?.disable();
          selectedDayControl?.disable();
        }
        else{
          this.scheduleForm?.get('monthlySelect').setValue(MonthlySelect.WeekAndDaySelected);
          selectedDateControl?.disable();
          selectedWeekControl?.enable();
          selectedDayControl?.enable();
          this.scheduleForm.get('selectedDay').setValue(this.DataToBePopulated?.event?.runConfig?.selectedDays[0]);
          this.scheduleForm.get('selectedWeek').setValue(this.DataToBePopulated?.event?.runConfig?.selectedWeek);
        }
        break;
      case "Yearly":
        this.scheduleForm.get('frequencyType').setValue(4);
        break;
      default:
        this.scheduleForm.get('frequencyType').setValue(2);
        break;
    }
    this.scheduleForm.get('runFrequency').setValue(this.DataToBePopulated?.event?.runFrequency);

    const minDate = new Date();
    minDate.setDate(minDate.getDate() + 2);
    const normalize = (d : Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const existingStartDate = normalize(new Date(this.DataToBePopulated?.event?.startDate));
    const existingEndDate = normalize(new Date(this.DataToBePopulated?.event?.endDate));

    this.scheduleForm.get('startDate').setValue(existingStartDate < minDate ? minDate : existingStartDate);
    this.scheduleForm.get('endDate').setValue(existingEndDate < minDate ? null : existingEndDate);
  }

  initializeForm() {
    this.minStartDate = new Date();
    this.minStartDate.setDate(this.minStartDate.getDate() + 2);
    this.minEndDate = this.minStartDate;
    this.scheduleForm = this.fb.group({
      scheduleName: ['', [Validators.maxLength(50), Validators.required]],
      startDate: [this.minStartDate, [Validators.required]],
      endDate: ['', [Validators.required]],
      runFrequency: [1, [Validators.required, Validators.min(1)]],
      frequencyType: [FrequencyType.Weekly, [Validators.required]],
      selectedDays: this.fb.array(
        this.daysOfWeek.map((_, index) => this.fb.control(index === this.minStartDate.getDay())),
        [atLeastOneDaySelected()]
      ),
      monthlySelect: [MonthlySelect.DateSelected, [Validators.required]],
      selectedWeek: [1, [Validators.required, Validators.min(1)]],
      selectedDate: [1, [Validators.required, Validators.min(1), Validators.max(31)]],
      selectedDay: [DaysType.Sunday, [Validators.required]],
      termsAndConditions: [false, [Validators.requiredTrue]],
    });
  }

  setUpFormStatusChange() {
    this.scheduleForm.statusChanges.subscribe(status => {
      this.disabledSaveButton = status === 'INVALID';
    });
  }

  setUpValueChanges() {
    this.scheduleForm.get('frequencyType')?.valueChanges.subscribe(() => {
      this.OnFrequencyTypeChange();
      this.resetDaysAndSetTodayIfWeekly();
    });

    this.scheduleForm.get('startDate')?.valueChanges.subscribe((startDate) => {
      const endDateControl = this.scheduleForm.get('endDate');
      if (new Date(endDateControl.value) < new Date(startDate)) {
        endDateControl.setValue(null);
      }
      this.minEndDate = new Date(this.scheduleForm.get('startDate')?.value as Date);
    });
  }

  onMonthlyDateInputChange(event: any): void {
    let value = event.target.value;
    if (parseInt(value) > 31) {
      event.target.value = value.substring(0, value.length-1);
    }

    if(parseInt(value) < 1){
      event.target.value = 1;
    }
  }

  resetDaysAndSetTodayIfWeekly() {
    const selectedDays = this.selectedDays;
    selectedDays.controls.forEach((control) => {
      control.setValue(false);
    });

    if (this.scheduleForm.get('frequencyType')?.value === FrequencyType.Weekly) {
      const todayIndex = new Date().getDay();
      selectedDays.at(todayIndex).setValue(true);
    }
  }

  get selectedDays(): FormArray {
    return this.scheduleForm.get('selectedDays') as FormArray;
  }

  OnFrequencyTypeChange() {
    const frequencyType = this.scheduleForm.get('frequencyType')?.value;
    const selectedDaysControl = this.scheduleForm.get('selectedDays');
    this.scheduleForm.get('runFrequency').setValue(1);
    this.scheduleForm.get('endDate').setValue('');
    this.scheduleForm.get('startDate').setValue(this.minStartDate);
    this.scheduleForm.get('termsAndConditions').setValue(false);

    if (frequencyType === FrequencyType.Weekly) {
      selectedDaysControl?.setValidators([Validators.required, atLeastOneDaySelected()]);
    } else {
      selectedDaysControl?.clearValidators();
    }
    selectedDaysControl?.updateValueAndValidity();
  }

  getMaxLength(): number {
    const frequencyType = this.scheduleForm.get('frequencyType')?.value;

    switch (frequencyType) {
      case FrequencyType.Daily:
        return 3;
      case FrequencyType.Weekly:
      case FrequencyType.Monthly:
      case FrequencyType.Yearly:
        return 2;
      default:
        return 3;
    }
  }

  restrictInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const maxLength = this.getMaxLength();

    this.ensureMaxLength(input, maxLength);
    this.removeLeadingZeros(input);
    this.clearEmptyInput(input);

    this.scheduleForm.get('runFrequency')?.setValue(Number(input.value));
  }

  private ensureMaxLength(input: HTMLInputElement, maxLength: number): void {
    if (input.value.length > maxLength) {
      input.value = input.value.slice(0, maxLength);
    }
  }

  private removeLeadingZeros(input: HTMLInputElement): void {
    if (input.value.startsWith('0')) {
      input.value = input.value.replace(/^0+/, '');
    }
  }

  private clearEmptyInput(input: HTMLInputElement): void {
    if (!input.value) {
      input.value = '';
    }
  }

  close() {
    this.ref.close();
  }

  submit(): void {
    const formData = this.prepareSubmitData();
    let scheduleOrderDetails = new ScheduleOrder();
    scheduleOrderDetails.scheduleName = formData.scheduleName;
    scheduleOrderDetails.startDate = (formData.startDate as Date).toLocaleDateString('en-CA');
    scheduleOrderDetails.endDate = (formData.endDate as Date).toLocaleDateString('en-CA');
    scheduleOrderDetails.frequencyType = formData.frequencyType;
    scheduleOrderDetails.runFrequency = formData.runFrequency;
    scheduleOrderDetails.orderId = formData.orderCode;
    scheduleOrderDetails.scheduleId = this.DataToBePopulated?.event?.scheduleId;
    if(this.scheduleForm?.get('monthlySelect').value == MonthlySelect.DateSelected){
      this.scheduleForm?.get('selectedDay')?.setValue(null);
      this.scheduleForm?.get('selectedWeek')?.setValue(null);
    }
    else if(this.scheduleForm?.get('monthlySelect').value == MonthlySelect.WeekAndDaySelected){
      this.scheduleForm?.get('selectedDate')?.setValue(null);
    }
    scheduleOrderDetails.runConfig = this.getRunConfig(scheduleOrderDetails, formData);
          

    if(this.DataToBePopulated == null || this.isDataEdited(scheduleOrderDetails)){
      this.orderCommandService.scheduleRecurrentOrder(scheduleOrderDetails).subscribe(()=>{
        this.ref.close(true);
      });
    }
    else{
      this.close();
    }
  }

  isDataEdited(scheduleOrderDetails){
    if(this.DataToBePopulated?.event?.scheduleName !== scheduleOrderDetails?.scheduleName ||
    this.DataToBePopulated?.event?.startDate !== scheduleOrderDetails.startDate ||
    this.DataToBePopulated?.event?.endDate !== scheduleOrderDetails.endDate ||
    this.DataToBePopulated?.event?.runFrequency !== scheduleOrderDetails?.runFrequency){ return true; }
    if(this.DataToBePopulated?.event?.frequencyType === "Weekly"){
      const currentSortedArray = this.DataToBePopulated?.event?.runConfig?.selectedDays?.sort();
      const newSortedArray = scheduleOrderDetails?.runConfig?.selectedDays?.sort();
      if(currentSortedArray.length !== newSortedArray.length || currentSortedArray.every((value, index) => value !== newSortedArray[index])){ return true; }
    }
    else if(this.DataToBePopulated?.event?.frequencyType === "Monthly"){
      if(this.DataToBePopulated?.event?.runConfig?.selectedDate !== scheduleOrderDetails?.runConfig?.selectedDate || 
        this.DataToBePopulated?.event?.runConfig?.selectedDay !== scheduleOrderDetails?.runConfig?.selectedDay ||
        this.DataToBePopulated?.event?.runConfig?.selectedWeek !== scheduleOrderDetails?.runConfig?.selectedWeek)
        { return true; }
    }
    return false;
  }

  getRunConfig(scheduleOrderDetails: ScheduleOrder, data:any): RunConfig{  
      switch(scheduleOrderDetails.frequencyType){
        case FrequencyType.Daily:
          return {};
        case FrequencyType.Weekly:
          return {selectedDays: data.selectedDaysEnum};
        case FrequencyType.Monthly:
          return {selectedDate: data.selectedDate, selectedDays:data.selectedDaysEnum, selectedWeek: data.selectedWeek};
        case FrequencyType.Yearly:
          let dateMonth = scheduleOrderDetails.startDate.split('-');
          return {selectedDate: parseInt(dateMonth[2]), selectedMonth: parseInt(dateMonth[1])};
        default: return{};
      }
    }

  private prepareSubmitData(): any {
    const selectedDaysIndices: DaysType[] = this.getSelectedDaysIndices();
    return {
      ...this.scheduleForm.value,
      selectedDaysEnum: selectedDaysIndices,
      orderCode: this.DataToBePopulated?.event?.orderId ?? this.currentOrder.currentOrder.OrderCode,
    };
  }

  private getSelectedDaysIndices(): DaysType[] {
    if (this.scheduleForm.get('frequencyType').value === FrequencyType.Weekly) {
      return this.selectedDays.controls.reduce((selectedDaysEnum: DaysType[], control, index) => {
        if (control.value) {
          selectedDaysEnum.push(index);
        }
        return selectedDaysEnum;
      }, []);
    }
    else if (this.scheduleForm.get('frequencyType').value === FrequencyType.Monthly && this.scheduleForm.get('monthlySelect').value === MonthlySelect.WeekAndDaySelected) {
      return [this.scheduleForm.get('selectedDay')?.value];
    }
    else {
      return [];
    }
  }
}
